import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Loader2, PlayCircle, CheckCircle2, FileText, ExternalLink, Banknote, UserCheck, Clock } from "lucide-react";
import { API, authHeaders, fileUrl } from "@/constants/roles";
import { fmtMoney } from "@/utils/currency";

const isVideo = (name = "") => /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(name);

const TEST_STATUS = {
  assigned: { label: "Sent — not started", cls: "border-sky-500/30 bg-sky-500/15 text-sky-300" },
  in_progress: { label: "In progress", cls: "border-sky-500/30 bg-sky-500/15 text-sky-300" },
  submitted: { label: "Submitted — needs review", cls: "border-amber-500/30 bg-amber-500/15 text-amber-300" },
  reviewed: { label: "Reviewed", cls: "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)]" },
  approved: { label: "Passed", cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300" }, // legacy
  rejected: { label: "Failed", cls: "border-rose-500/30 bg-rose-500/15 text-rose-300" }, // legacy
};

const agreedAmount = (test) => test?.agreed_payment ?? test?.pack_snapshot?.payment_amount ?? 20;

// Lifecycle stage chip: APPLIED → PORTFOLIO REVIEW → TEST SENT/IN PROGRESS → TEST SUBMITTED → REVIEWED → HIRED/REJECTED
export const hiringStage = (app, test) => {
  if (app.status === "accepted") return "Approved";
  if (app.status === "rejected") return "Rejected";
  if (test) {
    if (test.status === "approved") return "Test passed — hire decision next"; // legacy
    if (test.status === "rejected") return "Test failed"; // legacy
    if (test.status === "reviewed") {
      const bits = [];
      bits.push(test.result === "passed" ? "Passed" : "Failed");
      if (test.completion_status === "accepted") bits.push("paid");
      else if (test.completion_status === "invalid") bits.push("not payable");
      return `Reviewed: ${bits.join(" · ")}`;
    }
    if (test.status === "submitted") return "Test review";
    if (test.status === "in_progress") return "Test in progress";
    return "Test sent";
  }
  if (app.test_path === "test") return "Test requested";
  if (app.status === "reviewing") return "Portfolio review";
  return "Applied";
};

// Dual-decision review: COMPLETION gates the agreed payment (accepted → queues owed exactly once),
// RESULT gates hiring (passed/failed). They are independent on purpose.
const ReviewForm = ({ test, app, busy, call }) => {
  const [completion, setCompletion] = useState("accepted");
  const [result, setResult] = useState("passed");
  const [notes, setNotes] = useState("");
  const amount = agreedAmount(test);

  const submit = () =>
    call(
      () =>
        axios.post(
          `${API}/staff/test-assignments/${test.id}/review`,
          { completion, result, notes },
          { headers: authHeaders() }
        ),
      completion === "accepted"
        ? `Review saved — ${result}. Agreed ${fmtMoney(amount)} queued for payout (owner marks it paid).`
        : `Review saved — ${result}, submission marked incomplete/invalid: no payment queued.`
    );

  return (
    <div data-testid={`test-review-form-${app.id}`} className="mt-3 space-y-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">1 · Is this a legitimate completed test attempt?</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          <button
            data-testid={`review-completion-accepted-${app.id}`}
            onClick={() => setCompletion("accepted")}
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors ${completion === "accepted" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}
          >
            Accept completed → queues {fmtMoney(amount)}
          </button>
          <button
            data-testid={`review-completion-invalid-${app.id}`}
            onClick={() => setCompletion("invalid")}
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors ${completion === "invalid" ? "border-rose-500/40 bg-rose-500/15 text-rose-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}
          >
            Incomplete / invalid → no payment
          </button>
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">2 · Hiring result (separate from payment)</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          <button
            data-testid={`review-result-passed-${app.id}`}
            onClick={() => setResult("passed")}
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors ${result === "passed" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}
          >
            Passed
          </button>
          <button
            data-testid={`review-result-failed-${app.id}`}
            onClick={() => setResult("failed")}
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-bold transition-colors ${result === "failed" ? "border-rose-500/40 bg-rose-500/15 text-rose-300" : "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}
          >
            Failed
          </button>
        </div>
      </div>
      <textarea
        data-testid={`review-notes-input-${app.id}`}
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Internal review notes (optional)…"
        className="w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
      />
      <button
        data-testid={`review-submit-${app.id}`}
        onClick={submit}
        disabled={busy}
        className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save review
      </button>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">
        Payment follows the agreed pack amount ({fmtMoney(amount)}) — a failed test is still paid when the attempt was genuinely completed.
      </p>
    </div>
  );
};

// Per-application test path panel: portfolio review OR hosted paid test (versioned pack, agreed amount).
export const TestPanel = ({ app, test, isOwner, canManage, canHire = false, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [packs, setPacks] = useState(null);

  // which packs exist + are enabled right now (only needed before a test is assigned)
  useEffect(() => {
    if (test || !canManage) return;
    Promise.all(
      ["Standard Animator", "High-Quality Animator"].map((t) =>
        axios.get(`${API}/staff/test-pack?tier=${encodeURIComponent(t)}`, { headers: authHeaders() }).then((r) => r.data?.current || null).catch(() => null)
      )
    ).then(([std, hq]) => setPacks({ "Standard Animator": std, "High-Quality Animator": hq }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test, canManage]);

  const packReady = (tier) => {
    if (!packs) return true; // still loading — backend validates anyway
    const p = packs[tier];
    return Boolean(p && p.enabled);
  };

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const hire = (tier) => {
    const label = tier === "hq" ? "High Quality Animator" : "Standard Animator";
    if (!window.confirm(`Hire ${app.name} as ${label}? Their account becomes a worker account and they get the welcome email.`)) return;
    call(
      () => axios.post(`${API}/staff/applications/${app.id}/hire`, { tier }, { headers: authHeaders() }),
      `${app.name} hired as ${label}.`
    );
  };

  if (!canManage) return null;
  const stage = hiringStage(app, test);
  const hireTierDefault = test?.pack_tier === "High-Quality Animator" ? "hq" : "standard";
  const passed = test && (test.result === "passed" || test.status === "approved");

  const hireButtons = canHire && app.status !== "accepted" && (!test || passed) && (app.role === "Standard Animator" || app.role === "High-Quality Animator" || test) && (
    <span className="flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1">
      <UserCheck className="ml-1 h-3 w-3 text-emerald-300" />
      <button
        data-testid={`hire-standard-btn-${app.id}`}
        onClick={() => hire("standard")}
        disabled={busy}
        className={`px-1.5 py-0.5 text-[11px] font-bold hover:text-emerald-100 disabled:opacity-40 ${hireTierDefault === "standard" ? "text-emerald-300" : "text-emerald-300/60"}`}
      >
        Hire as Standard
      </button>
      <span className="text-emerald-500/50">·</span>
      <button
        data-testid={`hire-hq-btn-${app.id}`}
        onClick={() => hire("hq")}
        disabled={busy}
        className={`px-1.5 py-0.5 text-[11px] font-bold hover:text-emerald-100 disabled:opacity-40 ${hireTierDefault === "hq" ? "text-emerald-300" : "text-emerald-300/60"}`}
      >
        Hire as HQ
      </button>
    </span>
  );

  if (!test) {
    const defaultTier = app.role === "High-Quality Animator" ? "High-Quality Animator" : "Standard Animator";
    const requestTest = (tier) => {
      const note = window.prompt(`Assign the ${tier} test to ${app.name}?\n\nOptional note shown above the test pack (leave blank for none):`, "");
      if (note === null) return;
      call(
        () => axios.post(`${API}/staff/applications/${app.id}/assign-test`, { brief: note, tier }, { headers: authHeaders() }),
        `${tier} test sent — the applicant sees the current ${tier} pack with its agreed payment.`
      );
    };
    return (
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span data-testid={`hiring-stage-${app.id}`} className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-cyan-300">{stage}</span>
        {app.test_path === "portfolio" ? (
          <span data-testid={`test-path-portfolio-${app.id}`} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-2.5 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-3)]">Portfolio path</span>
        ) : (
          <button data-testid={`portfolio-path-btn-${app.id}`} onClick={() => call(() => axios.post(`${API}/staff/applications/${app.id}/portfolio-path`, {}, { headers: authHeaders() }), "Marked for portfolio review.")} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400">
            <FileText className="h-3 w-3" /> Portfolio review
          </button>
        )}
        <span className="flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-1">
          <FlaskConical className="ml-1 h-3 w-3 text-violet-300" />
          <button data-testid={`assign-test-btn-${app.id}`} onClick={() => requestTest(defaultTier)} disabled={!packReady(defaultTier)}
            title={packReady(defaultTier) ? `Send the current ${defaultTier} test pack` : `The ${defaultTier} pack is not enabled — the owner must enable it first`}
            className="px-1.5 py-0.5 text-[11px] font-bold text-violet-300 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-40">
            Send {defaultTier === "High-Quality Animator" ? "HQ" : "Standard"} test pack
          </button>
          <span className="text-violet-500/50">·</span>
          <button data-testid={`assign-test-other-${app.id}`} onClick={() => requestTest(defaultTier === "Standard Animator" ? "High-Quality Animator" : "Standard Animator")}
            disabled={!packReady(defaultTier === "Standard Animator" ? "High-Quality Animator" : "Standard Animator")}
            title={packReady(defaultTier === "Standard Animator" ? "High-Quality Animator" : "Standard Animator") ? "Send the other tier's pack instead" : "That pack is not enabled — the owner must enable it first"}
            className="px-1.5 py-0.5 text-[11px] font-bold text-violet-300/70 hover:text-violet-100 disabled:cursor-not-allowed disabled:opacity-40">
            use {defaultTier === "Standard Animator" ? "HQ" : "Standard"} instead
          </button>
        </span>
        {hireButtons}
        {packs && (!packReady("Standard Animator") || !packReady("High-Quality Animator")) && (
          <span data-testid={`test-pack-unavailable-${app.id}`} className="text-[10px] font-semibold text-[var(--pv-text-4)]">a pack is currently disabled — owner enables it in Test Pack</span>
        )}
      </div>
    );
  }

  const st = TEST_STATUS[test.status] || TEST_STATUS.assigned;
  return (
    <div data-testid={`test-panel-${app.id}`} className="mt-4 rounded-2xl border border-violet-500/20 bg-violet-500/5 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <FlaskConical className="h-3.5 w-3.5 text-violet-300" />
        <span data-testid={`hiring-stage-${app.id}`} className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-cyan-300">{stage}</span>
        <span data-testid={`test-status-${app.id}`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
        {test.pack_tier && (
          <span data-testid={`test-pack-tier-${app.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${test.pack_tier === "High-Quality Animator" ? "border-violet-500/30 bg-violet-500/15 text-violet-300" : "border-cyan-500/30 bg-cyan-500/15 text-cyan-300"}`}>{test.pack_tier === "High-Quality Animator" ? "HQ" : "Standard"} test</span>
        )}
        {test.pack_version != null && (
          <span data-testid={`test-pack-version-${app.id}`} className="rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-4)]">pack v{test.pack_version}</span>
        )}
        <span data-testid={`test-agreed-payment-${app.id}`} className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
          <Banknote className="h-3 w-3" /> agreed {fmtMoney(agreedAmount(test))}
        </span>
        {test.deadline_at && ["assigned", "in_progress"].includes(test.status) && (
          <span data-testid={`test-deadline-${app.id}`} className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${test.deadline_at < new Date().toISOString() ? "border-rose-500/30 bg-rose-500/10 text-rose-300" : "border-[var(--pv-border)] text-[var(--pv-text-4)]"}`}>
            <Clock className="h-3 w-3" /> due {new Date(test.deadline_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
        )}
        {test.created_at && (
          <span data-testid={`test-sent-${app.id}`} className="rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-4)]">
            sent {new Date(test.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
        )}
        {test.file_path && (
          <a data-testid={`test-play-${app.id}`} href={fileUrl(test.file_path)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/20">
            {isVideo(test.filename || "") ? <PlayCircle className="h-3 w-3" /> : <FileText className="h-3 w-3" />} {test.filename || "View submission"}
          </a>
        )}
        {test.external_url && (
          <a data-testid={`test-external-${app.id}`} href={test.external_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/20">
            <ExternalLink className="h-3 w-3" /> External link
          </a>
        )}
        {hireButtons}
      </div>
      {test.pack_snapshot?.title && (
        <p className="mt-2 text-xs text-[var(--pv-text-4)]"><span className="font-bold text-[var(--pv-text-3)]">Pack:</span> {test.pack_snapshot.title}{test.pack_snapshot.required_length ? ` · ${test.pack_snapshot.required_length}` : ""}</p>
      )}
      {test.brief && <p className="mt-1 line-clamp-2 text-xs text-[var(--pv-text-4)]"><span className="font-bold text-[var(--pv-text-3)]">Note:</span> {test.brief}</p>}
      {test.note && <p className="mt-1 line-clamp-2 text-xs text-[var(--pv-text-4)]"><span className="font-bold text-[var(--pv-text-3)]">Applicant note:</span> {test.note}</p>}
      {test.status === "reviewed" && (
        <p data-testid={`test-review-outcome-${app.id}`} className="mt-2 rounded-xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-2 text-xs text-[var(--pv-text-3)]">
          <span className="font-bold">Review:</span> {test.result === "passed" ? "Passed" : "Failed"}
          {" · "}
          {test.completion_status === "accepted" ? `completed attempt accepted — ${fmtMoney(agreedAmount(test))} ${test.payment_id ? "queued" : "payable"}` : "incomplete/invalid — no payment"}
          {test.review_notes ? ` — ${test.review_notes}` : ""}
        </p>
      )}
      {test.status === "rejected" && test.feedback && (
        <p className="mt-1 text-xs text-rose-300/80"><span className="font-bold">Feedback:</span> {test.feedback}</p>
      )}

      {test.status === "submitted" && <ReviewForm test={test} app={app} busy={busy} call={call} />}
    </div>
  );
};
